/**
 * Подключение движка синхронизации к состоянию приложения.
 *
 * Разделено с bootstrap, чтобы стартовая последовательность читалась
 * как последовательность, а не как клубок обработчиков.
 */
import type { EngineCallbacks } from '@shared/sync/engine'

import { useAppStore } from '../model/app-store'
import { emitInAppEvent } from '../model/in-app-events'
import { refreshMe } from './refresh-me'
import {
  handleRestrictedAccountState,
  handleSessionLost,
  handleTerminalAccountState,
} from './session-lifecycle'

export function attachEngineCallbacks(userId: string): EngineCallbacks {
  const store = () => useAppStore.getState()

  return {
    /*
     * §8.6: внутренний баннер N-06 — новое сообщение другого диалога и новый
     * match. Только в установившемся режиме: события догона (SYS-11) описывают
     * то, что произошло, пока приложение было закрыто, и баннерами не являются.
     */
    onEffects: (effects) => {
      // Lifecycle effects обрабатываются и во время replay/background.
      // Они управляют доступом к аккаунту, а не визуальным баннером.
      if (effects.accountStatus) {
        const account = effects.accountStatus
        const current = store().me
        if (current) {
          store().setMe({
            ...current,
            user: { ...current.user, status: account.status },
            sanctions: account.sanctions,
          })
        }

        const banned = account.sanctions.some((sanction) => sanction.type === 'banned')
        if (account.status === 'suspended' && banned) {
          store().setBlocking({ kind: 'account_restricted' })
          handleRestrictedAccountState(userId)
        } else if (account.status === 'suspended') {
          store().setBlocking({ kind: 'account_suspended' })
          handleTerminalAccountState(userId, 'account_suspended')
        } else if (account.status === 'deletion_pending' || account.status === 'deleted') {
          store().setBlocking({ kind: 'account_pending_deletion' })
          handleTerminalAccountState(userId, 'account_pending_deletion')
        } else if (banned) {
          store().setBlocking({ kind: 'account_restricted' })
          handleRestrictedAccountState(userId)
        }
      }

      const status = store().syncStatus
      if (status !== 'live' && status !== 'polling_fallback') return
      if (document.visibilityState !== 'visible' || !document.hasFocus()) return

      if (effects.incomingMessage) emitInAppEvent({ kind: 'message', message: effects.incomingMessage })
      if (effects.matchCreated) emitInAppEvent({ kind: 'match', match: effects.matchCreated })
    },

    onStatusChange: (status) => {
      store().setSyncStatus(status)

      // CC §3.2: online — это успешный обмен с сервером, а не navigator.onLine.
      store().setOnline(status === 'live' || status === 'polling_fallback')
    },

    onSessionLost: () => {
      void handleSessionLost(userId)
    },

    onConsentRequired: () => {
      store().setBlocking({ kind: 'consent_required' })
    },

    onClientUpgradeRequired: () => {
      store().setBlocking({ kind: 'client_upgrade_required' })
      void navigator.serviceWorker
        ?.getRegistration()
        .then((registration) => registration?.update())
        .catch(() => undefined)
    },

    onForbidden: () => {
      // 4403: причина выясняется через GET /me — consent, onboarding или Origin.
      void refreshMe()
    },
  }
}
